<script lang="ts" setup>
import type { DynamicAuctionItemList } from '@autobid/strapi-integration/rest/components'
import type { CarsListCar, SearchListCar } from '@autobid/ui/types/Car'
import { useAutobidAuth } from '@autobid/nuxt-auth/src/composables/useAutobidAuth'
import { useCurrentAuctionState } from '@autobid/ui/composables/car/useCurrentAuctionState'
import { getColorBasedOnState } from '@autobid/ui/utils/car/getColor'
import { computed, inject } from 'vue'

interface Props {
  car: CarsListCar | SearchListCar
}

const props = defineProps<Props>()

const { isAuthed } = useAutobidAuth()
const auctionStrapiData = inject<DynamicAuctionItemList>('auction-strapi-data')

const state = useCurrentAuctionState(computed(() => props.car))
</script>

<template>
  <div
    class="flex w-full flex-col xl:mt-0 xl:w-auto"
    :class="{
      'md:w-1/3': auctionStrapiData?.type === 'carList',
      'md:w-1/4': auctionStrapiData?.type === 'carSearch',
    }"
    v-bind="$attrs"
  >
    <span class="text-sm md:text-base">{{ $t('cars-list.starting-price') }}:</span>
    <span class="mb-1 block text-sm font-bold md:text-xl xl:max-w-[110px]">
      <CommonCalculatedPrice :price="car.price.start" />
    </span>

    <ElementsCarVatLink
      class="inline-block self-start text-[10px] underline md:text-xs"
      :car="car"
      :tax-popup-slug="auctionStrapiData?.taxPopupSlug"
    />
  </div>

  <p
    class="mb-3 flex w-full items-center justify-between md:mb-0 md:items-start md:justify-start xl:w-auto"
    :class="{
      'md:w-1/3': auctionStrapiData?.type === 'carList',
      'md:w-1/4': auctionStrapiData?.type === 'carSearch',
    }"
    v-bind="$attrs"
  >
    <span class="flex flex-col">
      <span>{{ $t('cars-list.current-bid') }}:</span>
      <span class="mb-1 flex justify-between font-bold">
        <transition
          enter-active-class="transition duration-300"
          enter-from-class="scale-125"
          enter-to-class="scale-100"
          leave-active-class="transition duration-300"
          leave-from-class="scale-100"
          leave-to-class="scale-125"
          mode="out-in"
        >
          <span
            :key="isAuthed ? car.price.current : 1"
            class="block xl:max-w-[110px]"
          >
            <CommonCalculatedPrice
              v-if="isAuthed"
              :price="car.price.current"
              class="md:text-xl"
              :class="
                car.price.current
                  ? getColorBasedOnState(car, state, car.price.current, 'text')
                  : undefined
              "
            />
            <span v-else>{{ $t('shared-car.confidential') }}</span>
          </span>
        </transition>
      </span>
    </span>
  </p>
</template>
